/**
 * PD-17646 — dates for the guest booking pages. A guest picks times in their own zone,
 * so which day a start falls on and how it reads both depend on the zone chosen.
 */

/** The calendar date (YYYY-MM-DD) of an instant in a zone. */
export function ymdInZone(instant: Date, timezone: string): string {
	return new Intl.DateTimeFormat('en-CA', {
		timeZone: timezone,
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
	}).format(instant);
}

/** Open starts by the day they fall on in this zone, each day in time order. */
export function groupStartsByDay(
	starts: ReadonlyArray<string>,
	timezone: string,
): Map<string, Array<string>> {
	const days = new Map<string, Array<string>>();

	for (const start of [...starts].sort()) {
		const ymd = ymdInZone(new Date(start), timezone);
		const day = days.get(ymd);

		if (day != null) {
			day.push(start);
		} else {
			days.set(ymd, [start]);
		}
	}

	return days;
}

export interface MonthCellModel {
	ymd: string;
	day: number;
}

/** A month as weeks, Sunday first, with null for the days outside it. */
export function monthWeeks(year: number, month: number): Array<Array<MonthCellModel | null>> {
	const firstWeekday = new Date(Date.UTC(year, month - 1, 1)).getUTCDay();
	const days = new Date(Date.UTC(year, month, 0)).getUTCDate();
	const cells: Array<MonthCellModel | null> = Array.from({ length: firstWeekday }, () => null);

	for (let day = 1; day <= days; day += 1) {
		cells.push({
			ymd: `${String(year)}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
			day,
		});
	}

	while (cells.length % 7 !== 0) {
		cells.push(null);
	}

	const weeks: Array<Array<MonthCellModel | null>> = [];

	for (let index = 0; index < cells.length; index += 7) {
		weeks.push(cells.slice(index, index + 7));
	}

	return weeks;
}

/** The instants a month's times are read for, a day wider so no time zone clips its ends. */
export function monthWindow(year: number, month: number): { from: string; to: string } {
	return {
		from: new Date(Date.UTC(year, month - 1, 0)).toISOString(),
		to: new Date(Date.UTC(year, month, 2)).toISOString(),
	};
}

export function formatTime(start: string, timezone: string): string {
	return new Intl.DateTimeFormat('en-US', {
		timeZone: timezone,
		hour: 'numeric',
		minute: '2-digit',
	}).format(new Date(start));
}

/** "Thursday 11/5", for a calendar date. */
export function formatDay(ymd: string): string {
	const [year = 0, month = 1, day = 1] = ymd.split('-').map(Number);
	const weekday = new Intl.DateTimeFormat('en-US', { weekday: 'long', timeZone: 'UTC' }).format(
		new Date(Date.UTC(year, month - 1, day)),
	);

	return `${weekday} ${String(month)}/${String(day)}`;
}

/** "Thursday 11/5/26 at 9:00 AM", in a zone. */
export function formatMeeting(start: string, timezone: string): string {
	const instant = new Date(start);
	const ymd = ymdInZone(instant, timezone);

	return `${formatDay(ymd)}/${ymd.slice(2, 4)} at ${formatTime(start, timezone)}`;
}

/** The zones offered: the guest's own, the host's and the US zones. */
export function timezoneChoices(browserZone: string, hostZone: string): Array<string> {
	return [
		...new Set([
			browserZone,
			hostZone,
			'America/New_York',
			'America/Chicago',
			'America/Denver',
			'America/Phoenix',
			'America/Los_Angeles',
		]),
	];
}

/** "Pacific Time": no season in the name, since a meeting can fall after the clocks change. */
export function timezoneLabel(timezone: string): string {
	const parts = new Intl.DateTimeFormat('en-US', {
		timeZone: timezone,
		timeZoneName: 'longGeneric',
	}).formatToParts(new Date());

	return parts.find((part) => part.type === 'timeZoneName')?.value ?? timezone;
}
